import { ArrowForward, ExpandLess, ExpandMore } from "@mui/icons-material";
import { Autocomplete, Box, Button, Card, CardContent, Grid, Stack, TextField, Typography } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Axios from "axios";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { toast } from "react-toastify";
import LoadingIcon from "src/Components/Common/LoadingIcon";
import useFetch from "src/Components/Common/useFetch";
import { VirtualizedAutocomplete } from "src/Components/Common/VirtualAutocomplete";
import CustomToast from "src/Components/CustomToast";
import instamartLogo from "src/Graphics/VendorPortal/instamartMain.png";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { convertUtcToIst } from "src/Utils/DateUtils";
import { formatMoney, ToDineroObj } from "src/Utils/MoneyUtils";
import { LoggedInSkeleton } from "../../Common/LoggedInSkeleton";
import { getReconTopBarButtons } from "../../Common/TopNavBar";
import { userContext } from "../../Contexts/userContext";

// ─── /api/ReturnLedgerSummary response shape ──
// summarisedARData/APData and dueAmountSummary values arrive as strings (Postgres numeric/bigint);
// OpeningBalance, closingBalance, duePayable, holdSummary, needsActionFromVendor arrive as numbers.
interface LedgerSummaryResponse {
  summarisedARData: {
    invoices_amount: string;
    invoices_entries: string;
    payments_amount: string;
    payments_entries: string;
    credit_notes_amount: string;
    credit_notes_entries: string;
    total_entries: string;
    net_amount: string;
  };
  summarisedAPData: {
    invoices_amount: string;
    invoices_entries: string;
    payment_amount: string;
    payment_entries: string;
    pr_rtv_amount: string;
    pr_rtv_entries: string;
    promo_amount: string;
    promo_entries: string;
    tds_amount: string;
    tds_entries: string;
    other_amount: string;
    other_entries: string;
    total_entries: string;
    net_amount: string;
    // Not populated by the backend yet — WIP badge shown until these land.
    ads_visibility_amount?: string;
    ads_visibility_entries?: string;
    payment_in_progress_amount?: string;
    payment_in_progress_entries?: string;
  };
  OpeningBalance: number;
  closingBalance: number;
  dueAmountSummary: {
    due_invoices_amount: string;
    due_invoices_entries: string;
    pr_rtv_amount: string;
    pr_rtv_entries: string;
    ads_visibility_amount: string;
    ads_visibility_entries: string;
    brand_promos_amount: string;
    brand_promos_entries: string;
    tds_amount: string;
    tds_entries: string;
    other_entries_amount: number;
    other_entries: number;
    advance_payments_amount: number;
    advance_payments_entries: number;
  };
  duePayable: number;
  holdSummary: {
    "GST Related Hold": number;
    "PR/RTV Related Hold": number;
    "Events Hold": number;
    "Ads Visibility Hold": number;
    "BDPO Hold": number;
    "Adhoc / Other Holds": number;
    isInHoldVendors: boolean;
  };
  needsActionFromVendor: {
    rejectedInvoices: { amount: number; count: number };
    actionOnHolds: { amount: number; count: number };
    totalAmount: number;
    totalCount: number;
  };
  reconciliationActions?: {
    actionsPendingOnVendor: { amount: number; count: number };
    actionsPendingOnUs: { amount: number; count: number };
  };
  otherIssuesRaised?: {
    actionsPendingOnVendor: { amount: number; count: number };
    actionsPendingOnUs: { amount: number; count: number };
  };
}

const num = (value: string | number | undefined | null) => Number(value ?? 0);

const ZONE_TONES = {
  filtered: { bg: "#e8ecff", color: "#2b3fc4" },
  snapshot: { bg: "#eef1f4", color: "#4a5568" },
  action: { bg: "#fdecef", color: "#b02a4a" },
} as const;

// ─── Zone header: title + a dotted pill describing what the figures below are scoped to ──
const ZoneScopePill = ({ tone, label }: { tone: keyof typeof ZONE_TONES; label: string }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.875,
      mt: 0.625,
      px: 1.5,
      py: 0.5,
      borderRadius: 999,
      fontSize: 13.5,
      fontWeight: 600,
      backgroundColor: ZONE_TONES[tone].bg,
      color: ZONE_TONES[tone].color,
    }}
  >
    <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "currentColor" }} />
    {label}
  </Box>
);

// ─── Divider between zones, previewing the tone of the section it introduces ──
const SectionBreak = ({ label, note, tone }: { label: string; note: string; tone: "snapshot" | "action" }) => (
  <Box sx={{ mt: 2, mb: 1, px: 0.25 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ flex: 1, height: 2, borderRadius: 1, backgroundColor: tone === "snapshot" ? "#d4dae1" : "#f3ccd5" }} />
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          px: 1.5,
          py: 0.375,
          borderRadius: 999,
          whiteSpace: "nowrap",
          backgroundColor: ZONE_TONES[tone].bg,
          color: ZONE_TONES[tone].color,
        }}
      >
        {label} <span style={{ fontSize: 12 }}>↓</span>
      </Box>
      <Box sx={{ flex: 1, height: 2, borderRadius: 1, backgroundColor: tone === "snapshot" ? "#d4dae1" : "#f3ccd5" }} />
    </Box>
    <Typography sx={{ mt: 0.5, textAlign: "center", fontSize: 11.5, fontWeight: 500, color: "text.secondary" }}>
      {note}
    </Typography>
  </Box>
);

// ─── Expand/collapse affordance — a labelled pill instead of a bare chevron icon ──
const ExpandToggle = ({ expanded }: { expanded: boolean }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.25,
      px: 1.25,
      py: 0.375,
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      whiteSpace: "nowrap",
      backgroundColor: expanded ? "rgba(0, 0, 0, 0.06)" : "rgba(61, 90, 254, 0.1)",
      color: expanded ? "rgba(0, 0, 0, 0.6)" : "#3d5afe",
    }}
  >
    {expanded ? "Hide" : "View"}
    {expanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
  </Box>
);

// ─── Badge for fields the backend hasn't populated yet ──
const WipBadge = () => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "#8a6d00",
      backgroundColor: "#fff3cd",
      borderRadius: "3px",
      px: 0.75,
      py: 0.25,
    }}
  >
    WIP
  </Box>
);

// ─── A single metric tile used inside the summary sections ──
const SummaryLeaf = ({
  label,
  amount,
  count,
  bold = false,
}: {
  label: string;
  amount: React.ReactNode;
  count?: number;
  bold?: boolean;
}) => (
  <Card sx={{ flex: "1 1 200px", border: "1px solid #e4e4e4" }}>
    <CardContent sx={{ p: 1.25 }}>
      <Typography className="body-m-bold" sx={{ color: bold ? "rgba(0,0,0,0.87)" : "text.secondary", mb: 1 }}>
        {label}
      </Typography>
      <Typography className="body-l-bold" sx={{ color: bold ? "rgba(0,0,0,0.87)" : "#575757" }}>
        {amount}
      </Typography>
      {count !== undefined && (
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "text.secondary", mt: 0.5 }}>Count: {count}</Typography>
      )}
    </CardContent>
  </Card>
);

const selectAllVendorConst = {
  vendorCode: "All",
  id: 232323,
  name: "Select All",
  firstLetter: "S",
};
const LedgerDownload = () => {
  const location = useLocation<any>();
  const history = useHistory();
  const todayDate = new Date();
  const { actor } = useContext(userContext);
  const accountName = actor?.name
    ?.toString()
    .toLowerCase()
    .replace(/[^a-z\d]+/gi, "");
  const [selectedVendor, setSelectedVendor] = useState<{
    id: number;
    name: string;
    vendorCode: string;
    firstLetter: string;
  }>(selectAllVendorConst);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [errorFutureDate, setErrorFutureDate] = useState<boolean>(false);
  const [errorEndFutureDate, setErrorEndFutureDate] = useState<boolean>(false);
  const [showLoadingIcon, setShowLoadingIcon] = useState(false);

  const [entitiesList, setEntitiesList] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState<{ companyId: string; companyName: string }>({
    companyId: "PAN-GROUPING",
    companyName: "PAN Grouping",
  });
  const [branchesList, setBranchesList] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState({ branchCode: "all", branchName: "Select All" });
  const [ownBusinessPartners, setOwnBusinessPartners] = useState([]);

  const [errorStartMsg, setErrorStartMsg] = useState("");
  const [errorEndMsg, setErrorEndMsg] = useState("");

  const [showSummary, setShowSummary] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [ledgerSummary, setLedgerSummary] = useState<LedgerSummaryResponse | null>(null);
  const [summaryExpanded, setSummaryExpanded] = useState({
    closingBalance: false,
    duePayable: false,
    needsAction: false,
  });
  const [dueInvoicesExpanded, setDueInvoicesExpanded] = useState(false);
  const [holdsExpanded, setHoldsExpanded] = useState(false);
  const [creditsDebitsExpanded, setCreditsDebitsExpanded] = useState(false);
  const [calcExpanded, setCalcExpanded] = useState(true);
  const [rejectedInvoicesExpanded, setRejectedInvoicesExpanded] = useState(false);
  const [reconciliationActionsExpanded, setReconciliationActionsExpanded] = useState(false);
  const [otherIssuesExpanded, setOtherIssuesExpanded] = useState(false);
  const [accountPayableExpanded, setAccountPayableExpanded] = useState(false);
  const [accountReceivablesExpanded, setAccountReceivablesExpanded] = useState(false);

  const toggleSummarySection = (section: keyof typeof summaryExpanded) => {
    setSummaryExpanded((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const holdBucketKeys = [
    "GST Related Hold",
    "PR/RTV Related Hold",
    "Events Hold",
    "Ads Visibility Hold",
    "BDPO Hold",
    "Adhoc / Other Holds",
  ] as const;
  const totalHolds = ledgerSummary ? holdBucketKeys.reduce((sum, k) => sum + num(ledgerSummary.holdSummary[k]), 0) : 0;
  const creditsDebitsTotal = ledgerSummary
    ? num(ledgerSummary.dueAmountSummary.pr_rtv_amount) +
      num(ledgerSummary.dueAmountSummary.ads_visibility_amount) +
      num(ledgerSummary.dueAmountSummary.brand_promos_amount) +
      num(ledgerSummary.dueAmountSummary.tds_amount) +
      num(ledgerSummary.dueAmountSummary.other_entries_amount) +
      num(ledgerSummary.dueAmountSummary.advance_payments_amount)
    : 0;
  const dueInvoicesAmount = ledgerSummary ? num(ledgerSummary.dueAmountSummary.due_invoices_amount) : 0;
  const duePayable = ledgerSummary?.duePayable ?? 0;
  const needsActionAmount =
    (ledgerSummary?.needsActionFromVendor?.totalAmount ?? 0) +
    (ledgerSummary?.reconciliationActions
      ? ledgerSummary.reconciliationActions.actionsPendingOnVendor.amount +
        ledgerSummary.reconciliationActions.actionsPendingOnUs.amount
      : 0) +
    (ledgerSummary?.otherIssuesRaised
      ? ledgerSummary.otherIssuesRaised.actionsPendingOnVendor.amount +
        ledgerSummary.otherIssuesRaised.actionsPendingOnUs.amount
      : 0);
  const needsActionCount =
    (ledgerSummary?.needsActionFromVendor?.totalCount ?? 0) +
    (ledgerSummary?.reconciliationActions
      ? ledgerSummary.reconciliationActions.actionsPendingOnVendor.count +
        ledgerSummary.reconciliationActions.actionsPendingOnUs.count
      : 0) +
    (ledgerSummary?.otherIssuesRaised
      ? ledgerSummary.otherIssuesRaised.actionsPendingOnVendor.count +
        ledgerSummary.otherIssuesRaised.actionsPendingOnUs.count
      : 0);

  const fetchLedgerSummary = async () => {
    setSummaryLoading(true);
    try {
      const response = await Axios.post(API_ENDPOINTS.RETURN_LEDGER_SUMMARY.url, {
        ownId: actor.id,
        startDate: startDate,
        endDate: endDate,
        businessPartnerId: selectedVendor.id !== 232323 ? selectedVendor.id : null,
        vendorCode: selectedVendor.vendorCode !== "All" ? selectedVendor.vendorCode : null,
        branchCode: selectedBranch.branchCode !== "all" ? selectedBranch.branchCode : null,
        companyCode: selectedEntity.companyId !== "all" ? selectedEntity.companyId : null,
      });
      setLedgerSummary(response.data.data);
      setShowSummary(true);
    } catch (error: any) {
      const message = error?.response?.data?.message || API_ENDPOINTS.RETURN_LEDGER_SUMMARY.failureMessage;
      toast.error(<CustomToast message={message} />);
    } finally {
      setSummaryLoading(false);
    }
  };

  const listCompanies = async () => {
    await useFetch(API_ENDPOINTS.LIST_COMPANIES.url, "GET", {
      failureMessage: API_ENDPOINTS.LIST_COMPANIES.failureMessage,
      thenCallBack: (response) => {
        setEntitiesList(response?.data?.companies);
      },
    });
  };
  const listBranches = async (companyId) => {
    await useFetch(API_ENDPOINTS.LIST_BRANCHES.url, "GET", {
      failureMessage: API_ENDPOINTS.LIST_BRANCHES.failureMessage,
      config: {
        params: {
          companyId,
        },
      },
      thenCallBack: (response) => {
        setBranchesList(response?.data?.branches);
      },
    });
  };
  const listAllBusinessPartners = async (companyId, branchCode) => {
    await useFetch(API_ENDPOINTS.LIST_ALL_BUSINESS_PARTNERS.url, "GET", {
      failureMessage: API_ENDPOINTS.LIST_ALL_BUSINESS_PARTNERS.failureMessage,
      config: {
        params: {
          ownId: actor.id,
          companyId: companyId !== "all" ? companyId : null,
          branchCode: branchCode !== "all" ? branchCode : null,
        },
      },
      thenCallBack: (response) => {
        setOwnBusinessPartners(response.data.partnerList);
      },
    });
  };
  const handleStartDateChange = (date) => {
    setStartDate(date);
    // Only reset end date if it's now before the start date
    if (date && endDate && date > endDate) {
      setEndDate(null);
    }
    setShowSummary(false);
  };
  const handleEndDateChange = (date) => {
    // Check if end date is not before start date
    if (date && startDate && date < startDate) {
      setEndDate(null);
      setShowSummary(false);
      return;
    }
    setEndDate(date);
    setShowSummary(false);
  };

  const downloadLedgerData = async () => {
    setShowLoadingIcon(true);

    try {
      const response = await Axios.post(
        API_ENDPOINTS.RETURN_LEDGER_DATA_AS_EXCEL.url,
        {
          ownId: actor.id,
          startDate: startDate,
          endDate: endDate,
          businessPartnerId: selectedVendor.id !== 232323 ? selectedVendor.id : null,
          vendorCode: selectedVendor.vendorCode !== "All" ? selectedVendor.vendorCode : null,
          branchCode: selectedBranch.branchCode !== "all" ? selectedBranch.branchCode : null,
          companyCode: selectedEntity.companyId !== "all" ? selectedEntity.companyId : null,
        },
        {
          responseType: "arraybuffer",
        }
      );

      const contentDisposition = response.headers["content-disposition"] || "";
      const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
      const fileName = fileNameMatch ? decodeURIComponent(fileNameMatch[1]) : "LedgerData.xlsx";
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(<CustomToast message={API_ENDPOINTS.RETURN_LEDGER_DATA_AS_EXCEL.failureMessage} />);
    } finally {
      setShowLoadingIcon(false);
    }
  };

  useEffect(() => {
    listCompanies();

    // Call APIs for default PAN-GROUPING entity
    if (actor.branchLevelReconcilation) {
      listBranches("PAN-GROUPING");
    } else {
      listAllBusinessPartners("PAN-GROUPING", "all");
    }
  }, []);

  return (
    <LoggedInSkeleton
      topBarButtons={getReconTopBarButtons("Ledger Analytics", actor.name, location?.state?.openCollapseObj, actor)}
      headerCenter={
        [9728, 1167419, 3914078].includes(actor.id) ? (
          <Box
            component="img"
            src={instamartLogo}
            alt="Instamart"
            sx={{ height: 24, width: "auto", objectFit: "contain" }}
          />
        ) : undefined
      }
    >
      <Grid>
        <Grid>
          <Card className="mt_20">
            <CardContent>
              <Grid container={true} spacing={2}>
                {/* First Part */}
                <Grid item={true} xs={4}>
                  <Grid container={true} spacing={2} className="pl_20">
                    <Grid
                      item={true}
                      xs={12}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Grid item={true} xs={4}>
                        <label className="fw_600">Entity Name:</label>
                      </Grid>
                      <Grid item={true} xs={8}>
                        <Autocomplete
                          size="small"
                          disableClearable={true}
                          disablePortal={true}
                          id="combo-box-demo"
                          options={[
                            { companyId: "PAN-GROUPING", companyName: "PAN Grouping" },
                            ...entitiesList.sort((a, b) => a.companyName.localeCompare(b.companyName)),
                          ]}
                          getOptionLabel={(option) => {
                            return option.companyName;
                          }}
                          value={selectedEntity}
                          //   sx={{ width: 300 }}
                          onChange={(_e, value) => {
                            setSelectedEntity(value);
                            setSelectedBranch({ branchCode: "all", branchName: "Select All" });
                            setSelectedVendor(selectAllVendorConst);
                            setShowSummary(false);
                            if (value.companyId !== "all") {
                              if (actor.branchLevelReconcilation) {
                                listBranches(value.companyId);
                              } else {
                                listAllBusinessPartners(value.companyId, "all");
                              }
                            }
                          }}
                          renderInput={(params) => <TextField {...params} placeholder="Entity Name" />}
                        />
                      </Grid>
                    </Grid>
                    {actor.branchLevelReconcilation && (
                      <Grid
                        item={true}
                        xs={12}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Grid item={true} xs={4}>
                          <label className="fw_600">Branch Name:</label>
                        </Grid>
                        <Grid item={true} xs={8}>
                          <Autocomplete
                            disabled={selectedEntity.companyId === "all"}
                            disableClearable={true}
                            size="small"
                            disablePortal={true}
                            id="combo-box-demo"
                            options={[{ branchCode: "all", branchName: "Select All" }, ...branchesList]}
                            getOptionLabel={(option) => {
                              return option.branchName;
                            }}
                            value={selectedBranch}
                            //   sx={{ width: 300 }}
                            onChange={(_e, value) => {
                              setSelectedBranch(value);
                              setSelectedVendor(selectAllVendorConst);
                              setShowSummary(false);
                              if (value.branchCode !== "all")
                                listAllBusinessPartners(selectedEntity.companyId, value.branchCode);
                            }}
                            renderInput={(params) => <TextField {...params} placeholder="Branch Name" />}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                {/* Second Part */}
                <Grid item={true} xs={4}>
                  <Grid container={true} spacing={2} className="pl_20">
                    <Grid
                      item={true}
                      xs={12}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Grid item={true} xs={4}>
                        <label className="fw_600">Vendor :</label>
                      </Grid>
                      <Grid item={true} xs={8}>
                        <VirtualizedAutocomplete
                          usedModule="recon"
                          options={[
                            selectAllVendorConst,
                            ...ownBusinessPartners.map((op) => {
                              return {
                                vendorCode: op.businessPartnerVendorCode,
                                id: op.businessPartnerId,
                                name: op.businessPartnerName,
                                firstLetter: op.businessPartnerName[0]?.toUpperCase(),
                              };
                            }),
                          ]}
                          value={selectedVendor}
                          onChange={(_e, value) => {
                            setShowSummary(false);
                            if (value === null) {
                              setSelectedVendor(selectAllVendorConst);
                              return;
                            }
                            setSelectedVendor(value);
                          }}
                          loading={false}
                          disabled={
                            (actor.branchLevelReconcilation && selectedBranch.branchCode === "all") ||
                            selectedEntity.companyId === "all"
                          }
                          placeholder="Search using PAN/Name of vendor"
                          label="Search using PAN/Name of vendor"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item={true}
                      xs={12}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      {/* <Grid item={true} xs={4}>
                        <label className="fw_600">Vendor Name:</label>
                      </Grid>
                      <Grid item={true} xs={8}>
                        <Autocomplete
                          disabled={vendorPan ? true : false}
                          size="small"
                          disablePortal={true}
                          id="combo-box-demo"
                          options={vendorNameArr}
                          value={vendorName}
                          //   sx={{ width: 300 }}
                          onChange={(_e, value) => setVendorName(value)}
                          renderInput={(params) => <TextField {...params} placeholder="Type Name" />}
                        />
                      </Grid> */}
                    </Grid>
                  </Grid>
                </Grid>

                {/* Third Part */}
                {/* <Grid item={true} xs={4}>
                  <Grid container={true} spacing={2} className="pl_20">
                    <Grid
                      item={true}
                      xs={12}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Grid item={true} xs={4}>
                        <label className="fw_600">Vendor PAN:</label>
                      </Grid>
                      <Grid item={true} xs={8}>
                        <Autocomplete
                          disabled={vendorName ? true : false}
                          size="small"
                          disablePortal={true}
                          id="combo-box-demo"
                          options={vendorPanArr}
                          value={vendorPan}
                          //   sx={{ width: 300 }}
                          onChange={(_e, value) => setVendorPan(value)}
                          renderInput={(params) => <TextField {...params} placeholder="Type PAN" />}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item={true}
                      xs={12}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Grid item={true} xs={4}>
                        <label className="fw_600">Vendor GST:</label>
                      </Grid>
                      <Grid item={true} xs={8}>
                        <Autocomplete
                          disabled={vendorCode ? true : false}
                          size="small"
                          disablePortal={true}
                          id="combo-box-demo"
                          options={vendorGstNumberArr}
                          value={vendorGstNumber}
                          //   sx={{ width: 300 }}
                          onChange={(_e, value) => setVendorGstNumber(value)}
                          renderInput={(params) => <TextField {...params} placeholder="Type GST No." />}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid> */}
              </Grid>

              <Grid sx={{ display: "flex", justifyContent: "start", paddingLeft: "20px", marginTop: "30px" }}>
                <Grid sx={{ mt: "16px" }}>
                  <label className="fw_600">Apply date filter on ledger entries</label>
                </Grid>
                <Grid xs={12} md={12} className="center_align ml_20">
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    // style={{ minWidth: "300px" }}
                    // className="closingblc_input"
                  >
                    <DesktopDatePicker
                      format="dd/MM/yyyy"
                      label="Start Date"
                      onError={(error) => {
                        if (error) setErrorStartMsg(error);
                        else setErrorStartMsg("");
                      }}
                      value={startDate}
                      onChange={(date: Date) => {
                        if (date !== null) {
                          if (date.getTime() > new Date().getTime()) {
                            setErrorFutureDate(true);
                          } else {
                            setErrorFutureDate(false);
                          }
                        }
                        handleStartDateChange(date);
                      }}
                      slotProps={{
                        textField: {
                          helperText: errorStartMsg
                            ? errorFutureDate
                              ? "Date cannot be in the future"
                              : "Invalid date format"
                            : "",
                        },
                      }}
                      maxDate={new Date(new Date().setDate(new Date().getDate() - 1))}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid xs={12} md={12} className="center_align ml_20">
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    // style={{ minWidth: "300px" }}
                    // className="closingblc_input"
                  >
                    <DesktopDatePicker
                      format="dd/MM/yyyy"
                      label="End Date"
                      onError={(error) => {
                        if (error) setErrorEndMsg(error);
                        else setErrorEndMsg("");
                      }}
                      value={endDate}
                      onChange={(date: Date) => {
                        if (date !== null) {
                          if (
                            moment(date).format("YYYY-MM-DD").valueOf() <
                            moment(new Date()).format("YYYY-MM-DD").valueOf()
                          ) {
                            setErrorEndFutureDate(false);
                          } else if (
                            moment(date).format("YYYY-MM-DD").valueOf() >
                            moment(new Date()).format("YYYY-MM-DD").valueOf()
                          ) {
                            // setErrorEndFutureDate(true);
                            // allow future end date
                            setErrorEndFutureDate(false);
                          } else {
                            setErrorEndFutureDate(false);
                          }
                        }
                        handleEndDateChange(date);
                      }}
                      slotProps={{
                        textField: {
                          helperText: errorEndMsg
                            ? errorEndFutureDate
                              ? "Date cannot be in the future"
                              : "Invalid date format"
                            : "",
                        },
                      }}
                      maxDate={new Date(new Date().setDate(new Date().getDate() - 1))}
                      minDate={startDate}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid className="mt_20 center_align_ver_horiz" sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button
            className="theme_btn"
            onClick={() => fetchLedgerSummary()}
            disabled={
              startDate === null ||
              endDate === null ||
              errorEndMsg !== "" ||
              errorEndFutureDate ||
              errorStartMsg !== "" ||
              errorFutureDate ||
              summaryLoading
            }
            style={{ width: "200px" }}
          >
            <LoadingIcon loading={summaryLoading} />
            Apply
          </Button>
          <Button
            className="theme_btn"
            onClick={() => {
              downloadLedgerData();
            }}
            disabled={
              startDate === null ||
              endDate === null ||
              errorEndMsg !== "" ||
              errorEndFutureDate ||
              errorStartMsg !== "" ||
              errorFutureDate ||
              showLoadingIcon
            }
            style={{ width: "200px" }}
          >
            <LoadingIcon loading={showLoadingIcon} />
            Download
          </Button>
        </Grid>

        {!showSummary && (
          <Box
            sx={{
              mt: 2.5,
              p: 5,
              border: "1px dashed rgba(0,0,0,0.15)",
              borderRadius: 1,
              textAlign: "center",
              backgroundColor: "#fafafa",
            }}
          >
            <Typography sx={{ fontSize: 17, fontWeight: 700 }}>
              Select a vendor and click Apply to view their ledger summary
            </Typography>
            <Typography sx={{ mt: 1, fontSize: 14, color: "text.secondary" }}>
              Search by PAN or name, choose a date range, then Apply. What you see matches exactly what the vendor sees
              in their portal. Use Download for the full ledger file.
            </Typography>
          </Box>
        )}

        {showSummary && ledgerSummary && (
          <Grid className="mt_20">
            {/* ============ ZONE 1 · LEDGER POSITION ============ */}
            <Box sx={{ mt: 0.5, mb: 1.5 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Ledger Position</Typography>
              <ZoneScopePill
                tone="filtered"
                label={`Based on date filter · ${startDate ? convertUtcToIst(startDate, "DD/MM/YYYY") : ""} – ${
                  endDate ? convertUtcToIst(endDate, "DD/MM/YYYY") : ""
                }`}
              />
            </Box>

            <Card sx={{ mb: 1.5, border: "1px solid #e0e0e0" }}>
              <CardContent sx={{ p: 1.25 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSummarySection("closingBalance")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Typography className="heading-m">Closing Balance</Typography>
                    <Typography className="body-m-medium" color="text.secondary">
                      as on {endDate ? convertUtcToIst(endDate, "DD/MM/YYYY") : ""}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography className="heading-m">
                      {formatMoney(ToDineroObj(ledgerSummary.closingBalance))}
                    </Typography>
                    <ExpandToggle expanded={summaryExpanded.closingBalance} />
                  </Box>
                </Box>

                {summaryExpanded.closingBalance && (
                  <>
                    <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mb: 1.5 }}>
                      <CardContent sx={{ p: 1.5 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography className="heading-s">Opening Balance</Typography>
                            <Typography className="body-m-medium" color="text.secondary">
                              as on {startDate ? convertUtcToIst(startDate, "DD/MM/YYYY") : ""}
                            </Typography>
                          </Box>
                          <Typography className="heading-s" color="text.secondary">
                            {formatMoney(ToDineroObj(ledgerSummary.OpeningBalance))}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>

                    <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mb: 1.5 }}>
                      <CardContent sx={{ p: 1.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                          onClick={() => setAccountPayableExpanded((prev) => !prev)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography className="heading-s">Account Payable</Typography>
                            <Typography className="body-m-medium" color="text.secondary">
                              {startDate ? convertUtcToIst(startDate, "DD/MM/YYYY") : ""} –{" "}
                              {endDate ? convertUtcToIst(endDate, "DD/MM/YYYY") : ""}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography className="heading-s" color="text.secondary">
                              {formatMoney(ToDineroObj(num(ledgerSummary.summarisedAPData.net_amount)))} (
                              {ledgerSummary.summarisedAPData.total_entries})
                            </Typography>
                            <ExpandToggle expanded={accountPayableExpanded} />
                          </Box>
                        </Box>
                        {accountPayableExpanded && (
                          <>
                            <Stack direction="row" spacing={1.25} sx={{ mt: 1.5, mb: 1.25 }}>
                              <SummaryLeaf
                                label="Invoices"
                                amount={formatMoney(ToDineroObj(num(ledgerSummary.summarisedAPData.invoices_amount)))}
                                count={num(ledgerSummary.summarisedAPData.invoices_entries)}
                              />
                              <SummaryLeaf
                                label="Payment Made"
                                amount={formatMoney(ToDineroObj(num(ledgerSummary.summarisedAPData.payment_amount)))}
                                count={num(ledgerSummary.summarisedAPData.payment_entries)}
                              />
                              <SummaryLeaf
                                label="PR/RTV"
                                amount={formatMoney(ToDineroObj(num(ledgerSummary.summarisedAPData.pr_rtv_amount)))}
                                count={num(ledgerSummary.summarisedAPData.pr_rtv_entries)}
                              />
                              <SummaryLeaf
                                label="Brand Promo (BDPO)"
                                amount={formatMoney(ToDineroObj(num(ledgerSummary.summarisedAPData.promo_amount)))}
                                count={num(ledgerSummary.summarisedAPData.promo_entries)}
                              />
                            </Stack>
                            <Stack direction="row" spacing={1.25}>
                              <SummaryLeaf
                                label="Payment in Progress"
                                amount={
                                  ledgerSummary.summarisedAPData.payment_in_progress_amount !== undefined ? (
                                    formatMoney(
                                      ToDineroObj(num(ledgerSummary.summarisedAPData.payment_in_progress_amount))
                                    )
                                  ) : (
                                    <WipBadge />
                                  )
                                }
                                count={
                                  ledgerSummary.summarisedAPData.payment_in_progress_entries !== undefined
                                    ? num(ledgerSummary.summarisedAPData.payment_in_progress_entries)
                                    : undefined
                                }
                              />
                              <SummaryLeaf
                                label="Ads & Visibility"
                                amount={
                                  ledgerSummary.summarisedAPData.ads_visibility_amount !== undefined ? (
                                    formatMoney(ToDineroObj(num(ledgerSummary.summarisedAPData.ads_visibility_amount)))
                                  ) : (
                                    <WipBadge />
                                  )
                                }
                                count={
                                  ledgerSummary.summarisedAPData.ads_visibility_entries !== undefined
                                    ? num(ledgerSummary.summarisedAPData.ads_visibility_entries)
                                    : undefined
                                }
                              />
                              <SummaryLeaf
                                label="TDS"
                                amount={formatMoney(ToDineroObj(num(ledgerSummary.summarisedAPData.tds_amount)))}
                                count={num(ledgerSummary.summarisedAPData.tds_entries)}
                              />
                              <SummaryLeaf
                                label="Other entries"
                                amount={formatMoney(ToDineroObj(num(ledgerSummary.summarisedAPData.other_amount)))}
                              />
                            </Stack>
                          </>
                        )}
                      </CardContent>
                    </Card>

                    <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)" }}>
                      <CardContent sx={{ p: 1.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                          onClick={() => setAccountReceivablesExpanded((prev) => !prev)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography className="heading-s">Account Receivables</Typography>
                            <Typography className="body-m-medium" color="text.secondary">
                              {startDate ? convertUtcToIst(startDate, "DD/MM/YYYY") : ""} –{" "}
                              {endDate ? convertUtcToIst(endDate, "DD/MM/YYYY") : ""}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography className="heading-s" color="text.secondary">
                              {formatMoney(ToDineroObj(num(ledgerSummary.summarisedARData.net_amount)))} (
                              {ledgerSummary.summarisedARData.total_entries})
                            </Typography>
                            <ExpandToggle expanded={accountReceivablesExpanded} />
                          </Box>
                        </Box>
                        {accountReceivablesExpanded && (
                          <Stack direction="row" spacing={1.25} sx={{ mt: 1.5 }}>
                            <SummaryLeaf
                              label="Invoices raised"
                              amount={formatMoney(ToDineroObj(num(ledgerSummary.summarisedARData.invoices_amount)))}
                              count={num(ledgerSummary.summarisedARData.invoices_entries)}
                            />
                            <SummaryLeaf
                              label="Payment received"
                              amount={formatMoney(ToDineroObj(num(ledgerSummary.summarisedARData.payments_amount)))}
                              count={num(ledgerSummary.summarisedARData.payments_entries)}
                            />
                            <SummaryLeaf
                              label="Credit note received"
                              amount={formatMoney(ToDineroObj(num(ledgerSummary.summarisedARData.credit_notes_amount)))}
                              count={num(ledgerSummary.summarisedARData.credit_notes_entries)}
                            />
                          </Stack>
                        )}
                      </CardContent>
                    </Card>
                  </>
                )}
              </CardContent>
            </Card>

            <SectionBreak
              label="Current Dues"
              tone="action"
              note="Due figures reflect your latest ledger position and don't change with the date filter above."
            />

            {/* ============ ZONE 2 · DUE POSITION ============ */}
            <Box sx={{ mt: 0.5, mb: 1.5 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Due Position</Typography>
              <ZoneScopePill
                tone="filtered"
                label={`Fixed snapshot · as on ${convertUtcToIst(todayDate, "DD/MM/YYYY")} (not based on date filter)`}
              />
            </Box>

            <Card sx={{ mb: 1.5, border: "1px solid #e0e0e0" }}>
              <CardContent sx={{ p: 1.25 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSummarySection("duePayable")}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography className="heading-m">Due Payable (- ve) / Receivable (+ ve)</Typography>
                      {ledgerSummary.holdSummary.isInHoldVendors && (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.5,
                            py: 0.25,
                            borderRadius: 2,
                            border: "1px solid #e53935",
                            backgroundColor: "#fff",
                          }}
                        >
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#e53935" }} />
                          <Typography variant="caption" sx={{ color: "#e53935", fontWeight: 600, fontSize: "0.75rem" }}>
                            Full Payment on Hold
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography className="body-m-medium" color="text.secondary">
                      What you're going to get, after holds · due as on {convertUtcToIst(todayDate, "DD/MM/YYYY")}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography className="heading-m">{formatMoney(ToDineroObj(duePayable))}</Typography>
                    <ExpandToggle expanded={summaryExpanded.duePayable} />
                  </Box>
                </Box>

                {summaryExpanded.duePayable && (
                  <>
                    <Box
                      sx={{
                        mt: 1.5,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "pointer",
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: "#3d5afe",
                      }}
                      onClick={() => setCalcExpanded((prev) => !prev)}
                    >
                      <ExpandMore
                        fontSize="small"
                        sx={{ transition: "transform 0.18s ease", transform: calcExpanded ? "rotate(180deg)" : "none" }}
                      />
                      View calculation
                    </Box>

                    {calcExpanded && (
                      <Box
                        sx={{
                          mt: 1.5,
                          backgroundColor: "#fafafa",
                          border: "1px solid rgba(0,0,0,0.12)",
                          borderRadius: 1,
                          p: "16px 20px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.375 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
                              Due Invoices
                            </Typography>
                            <Typography sx={{ fontSize: 17, fontWeight: 600 }}>
                              {formatMoney(ToDineroObj(Math.abs(dueInvoicesAmount)))}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: 24, fontWeight: 600, color: "text.secondary", lineHeight: 1 }}>
                            −
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.375 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
                              Credits/Debits Yet to be Adjusted
                            </Typography>
                            <Typography sx={{ fontSize: 17, fontWeight: 600 }}>
                              {formatMoney(ToDineroObj(creditsDebitsTotal))}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: 24, fontWeight: 600, color: "text.secondary", lineHeight: 1 }}>
                            −
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.375 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "rgba(0,0,0,0.87)" }}>
                              Holds on Payable
                            </Typography>
                            <Typography sx={{ fontSize: 17, fontWeight: 700 }}>
                              {formatMoney(ToDineroObj(totalHolds))}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: 24, fontWeight: 600, color: "text.secondary", lineHeight: 1 }}>
                            =
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.375,
                              ml: "auto",
                              textAlign: "right",
                            }}
                          >
                            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                              {duePayable < 0 ? "Due Payable" : "Due Receivable"}
                            </Typography>
                            <Typography sx={{ fontSize: 21, fontWeight: 700 }}>
                              {formatMoney(ToDineroObj(duePayable))}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography
                          sx={{
                            mt: 1.25,
                            pt: 1.25,
                            borderTop: "1px dashed rgba(0,0,0,0.12)",
                            fontSize: 12,
                            color: "text.secondary",
                          }}
                        >
                          Due Invoices is shown as a negative figure in the ledger (−
                          {formatMoney(ToDineroObj(Math.abs(dueInvoicesAmount)))}); its magnitude is used in this
                          calculation.
                        </Typography>
                      </Box>
                    )}

                    <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mt: 1 }}>
                      <CardContent sx={{ p: 1.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            cursor: "pointer",
                          }}
                          onClick={() => setDueInvoicesExpanded((prev) => !prev)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography className="heading-s">Due Invoices</Typography>
                            <Typography className="body-m-medium" color="text.secondary">
                              due as on {convertUtcToIst(todayDate, "DD/MM/YYYY")}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography className="heading-s">
                              {formatMoney(ToDineroObj(-Math.abs(dueInvoicesAmount)))} (
                              {num(ledgerSummary.dueAmountSummary.due_invoices_entries)})
                            </Typography>
                            <ExpandToggle expanded={dueInvoicesExpanded} />
                          </Box>
                        </Box>
                        {dueInvoicesExpanded && (
                          <Stack direction="row" spacing={1.25} sx={{ mt: 1.5 }}>
                            <Box sx={{ flex: "0 1 320px" }}>
                              <SummaryLeaf
                                label="Overdue Invoices"
                                amount={formatMoney(ToDineroObj(-Math.abs(dueInvoicesAmount)))}
                                count={num(ledgerSummary.dueAmountSummary.due_invoices_entries)}
                              />
                            </Box>
                          </Stack>
                        )}
                      </CardContent>
                    </Card>

                    <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mt: 1 }}>
                      <CardContent sx={{ p: 1.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            cursor: "pointer",
                          }}
                          onClick={() => setCreditsDebitsExpanded((prev) => !prev)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography className="heading-s">Credits/Debits Yet to be Adjusted</Typography>
                            <Typography className="body-m-medium" color="text.secondary">
                              as on {convertUtcToIst(todayDate, "DD/MM/YYYY")}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography className="heading-s">
                              {formatMoney(ToDineroObj(creditsDebitsTotal))} (
                              {num(ledgerSummary.dueAmountSummary.pr_rtv_entries) +
                                num(ledgerSummary.dueAmountSummary.ads_visibility_entries) +
                                num(ledgerSummary.dueAmountSummary.brand_promos_entries) +
                                num(ledgerSummary.dueAmountSummary.tds_entries) +
                                num(ledgerSummary.dueAmountSummary.other_entries) +
                                num(ledgerSummary.dueAmountSummary.advance_payments_entries)}
                              )
                            </Typography>
                            <ExpandToggle expanded={creditsDebitsExpanded} />
                          </Box>
                        </Box>
                        {creditsDebitsExpanded && (
                          <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                            <SummaryLeaf
                              label="PR/RTV"
                              amount={formatMoney(ToDineroObj(num(ledgerSummary.dueAmountSummary.pr_rtv_amount)))}
                              count={num(ledgerSummary.dueAmountSummary.pr_rtv_entries)}
                            />
                            <SummaryLeaf
                              label="Ads & Visibility"
                              amount={formatMoney(
                                ToDineroObj(num(ledgerSummary.dueAmountSummary.ads_visibility_amount))
                              )}
                              count={num(ledgerSummary.dueAmountSummary.ads_visibility_entries)}
                            />
                            <SummaryLeaf
                              label="Brand Promos (BDPO)"
                              amount={formatMoney(ToDineroObj(num(ledgerSummary.dueAmountSummary.brand_promos_amount)))}
                              count={num(ledgerSummary.dueAmountSummary.brand_promos_entries)}
                            />
                            <SummaryLeaf
                              label="TDS"
                              amount={formatMoney(ToDineroObj(num(ledgerSummary.dueAmountSummary.tds_amount)))}
                              count={num(ledgerSummary.dueAmountSummary.tds_entries)}
                            />
                            <SummaryLeaf
                              label="Other Entries"
                              amount={formatMoney(
                                ToDineroObj(num(ledgerSummary.dueAmountSummary.other_entries_amount))
                              )}
                              count={num(ledgerSummary.dueAmountSummary.other_entries)}
                            />
                            <SummaryLeaf
                              label="Advance Payment (to be adjusted)"
                              amount={formatMoney(
                                ToDineroObj(num(ledgerSummary.dueAmountSummary.advance_payments_amount))
                              )}
                              count={num(ledgerSummary.dueAmountSummary.advance_payments_entries)}
                            />
                          </Stack>
                        )}
                      </CardContent>
                    </Card>

                    <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mt: 1 }}>
                      <CardContent sx={{ p: 1.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            cursor: "pointer",
                          }}
                          onClick={() => setHoldsExpanded((prev) => !prev)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography className="heading-s">Holds on Payable</Typography>
                            <Typography className="body-m-medium" color="text.secondary">
                              as on {convertUtcToIst(todayDate, "DD/MM/YYYY")}
                            </Typography>
                            <Button
                              size="small"
                              variant="contained"
                              endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                              sx={{
                                borderRadius: 0,
                                textTransform: "none",
                                fontSize: 11.5,
                                lineHeight: 1.4,
                                px: 1,
                                py: 0.25,
                                minWidth: 0,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                history.push({
                                  pathname: `/${accountName}/disputeResolution/vendorHold`,
                                  state:
                                    selectedVendor.vendorCode !== "All"
                                      ? { pan: selectedVendor.vendorCode }
                                      : undefined,
                                });
                              }}
                            >
                              View Vendor Holds
                            </Button>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography className="heading-s">{formatMoney(ToDineroObj(totalHolds))}</Typography>
                            <ExpandToggle expanded={holdsExpanded} />
                          </Box>
                        </Box>
                        <Typography sx={{ mt: 0.5, fontSize: 12, fontWeight: 600, color: "rgba(0,0,0,0.87)" }}>
                          In this PR/RTV, Ads Visibility, BDPO, Event Holds are entries yet to be accounted and pending
                          confirmation
                        </Typography>
                        {holdsExpanded && (
                          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5, mt: 1.5 }}>
                            {holdBucketKeys.map((key) => (
                              <SummaryLeaf
                                key={key}
                                label={key}
                                bold
                                amount={formatMoney(ToDineroObj(num(ledgerSummary.holdSummary[key])))}
                              />
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </>
                )}
              </CardContent>
            </Card>

            <SectionBreak
              label="Your Action Needed"
              tone="action"
              note="These items are waiting on you — acting on them moves money forward."
            />

            {/* ============ ZONE 3 · NEEDS ACTION FROM VENDOR TO PROCESS ============ */}
            <Box sx={{ mt: 0.5, mb: 1.5 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Actions Needed/Pending from Vendor</Typography>
              <ZoneScopePill
                tone="action"
                label={`Requires vendor action to move forward · as on ${
                  endDate ? convertUtcToIst(endDate, "DD/MM/YYYY") : ""
                }`}
              />
            </Box>

            <Card sx={{ mb: 1.5, border: "1px solid #e0e0e0" }}>
              <CardContent sx={{ p: 1.25 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSummarySection("needsAction")}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Typography className="heading-m">Needs Action</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography className="heading-m">
                      {formatMoney(ToDineroObj(needsActionAmount))} ({needsActionCount})
                    </Typography>
                    <ExpandToggle expanded={summaryExpanded.needsAction} />
                  </Box>
                </Box>

                {summaryExpanded.needsAction && (
                  <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mt: 1.5 }}>
                    <CardContent sx={{ p: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                          cursor: "pointer",
                        }}
                        onClick={() => setRejectedInvoicesExpanded((prev) => !prev)}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography className="heading-s">Rejected invoices and holds awaiting action</Typography>
                          <Typography className="body-m-medium" color="text.secondary">
                            as on {endDate ? convertUtcToIst(endDate, "DD/MM/YYYY") : ""}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography className="heading-s">
                            {formatMoney(
                              ToDineroObj(
                                ledgerSummary.needsActionFromVendor.rejectedInvoices.amount +
                                  ledgerSummary.needsActionFromVendor.actionOnHolds.amount
                              )
                            )}{" "}
                            (
                            {ledgerSummary.needsActionFromVendor.rejectedInvoices.count +
                              ledgerSummary.needsActionFromVendor.actionOnHolds.count}
                            )
                          </Typography>
                          <ExpandToggle expanded={rejectedInvoicesExpanded} />
                        </Box>
                      </Box>
                      {rejectedInvoicesExpanded && (
                        <Stack direction="row" spacing={1.25} sx={{ mt: 1.5 }}>
                          <SummaryLeaf
                            label="Rejected Invoice Amount"
                            amount={formatMoney(
                              ToDineroObj(ledgerSummary.needsActionFromVendor.rejectedInvoices.amount)
                            )}
                            count={ledgerSummary.needsActionFromVendor.rejectedInvoices.count}
                          />
                          <SummaryLeaf
                            label="Action on Holds"
                            amount={formatMoney(ToDineroObj(ledgerSummary.needsActionFromVendor.actionOnHolds.amount))}
                            count={ledgerSummary.needsActionFromVendor.actionOnHolds.count}
                          />
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                )}

                {summaryExpanded.needsAction && (
                  <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mt: 1.5 }}>
                    <CardContent sx={{ p: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                          cursor: "pointer",
                        }}
                        onClick={() => setReconciliationActionsExpanded((prev) => !prev)}
                      >
                        <Typography className="heading-s">Reconciliation Actions</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {ledgerSummary.reconciliationActions ? (
                            <Typography className="heading-s">
                              {formatMoney(
                                ToDineroObj(
                                  ledgerSummary.reconciliationActions.actionsPendingOnVendor.amount +
                                    ledgerSummary.reconciliationActions.actionsPendingOnUs.amount
                                )
                              )}{" "}
                              (
                              {ledgerSummary.reconciliationActions.actionsPendingOnVendor.count +
                                ledgerSummary.reconciliationActions.actionsPendingOnUs.count}
                              )
                            </Typography>
                          ) : (
                            <WipBadge />
                          )}
                          <ExpandToggle expanded={reconciliationActionsExpanded} />
                        </Box>
                      </Box>
                      {reconciliationActionsExpanded && (
                        <Stack direction="row" spacing={1.25} sx={{ mt: 1.5 }}>
                          <SummaryLeaf
                            label="Actions Pending on Vendor"
                            amount={
                              ledgerSummary.reconciliationActions ? (
                                formatMoney(
                                  ToDineroObj(ledgerSummary.reconciliationActions.actionsPendingOnVendor.amount)
                                )
                              ) : (
                                <WipBadge />
                              )
                            }
                            count={ledgerSummary.reconciliationActions?.actionsPendingOnVendor.count}
                          />
                          <SummaryLeaf
                            label="Actions Pending on Us"
                            amount={
                              ledgerSummary.reconciliationActions ? (
                                formatMoney(ToDineroObj(ledgerSummary.reconciliationActions.actionsPendingOnUs.amount))
                              ) : (
                                <WipBadge />
                              )
                            }
                            count={ledgerSummary.reconciliationActions?.actionsPendingOnUs.count}
                          />
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                )}

                {summaryExpanded.needsAction && (
                  <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mt: 1.5 }}>
                    <CardContent sx={{ p: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                          cursor: "pointer",
                        }}
                        onClick={() => setOtherIssuesExpanded((prev) => !prev)}
                      >
                        <Typography className="heading-s">Other Issues Raised</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {ledgerSummary.otherIssuesRaised ? (
                            <Typography className="heading-s">
                              {formatMoney(
                                ToDineroObj(
                                  ledgerSummary.otherIssuesRaised.actionsPendingOnVendor.amount +
                                    ledgerSummary.otherIssuesRaised.actionsPendingOnUs.amount
                                )
                              )}{" "}
                              (
                              {ledgerSummary.otherIssuesRaised.actionsPendingOnVendor.count +
                                ledgerSummary.otherIssuesRaised.actionsPendingOnUs.count}
                              )
                            </Typography>
                          ) : (
                            <WipBadge />
                          )}
                          <ExpandToggle expanded={otherIssuesExpanded} />
                        </Box>
                      </Box>
                      {otherIssuesExpanded && (
                        <Stack direction="row" spacing={1.25} sx={{ mt: 1.5 }}>
                          <SummaryLeaf
                            label="Actions Pending on Vendor"
                            amount={
                              ledgerSummary.otherIssuesRaised ? (
                                formatMoney(ToDineroObj(ledgerSummary.otherIssuesRaised.actionsPendingOnVendor.amount))
                              ) : (
                                <WipBadge />
                              )
                            }
                            count={ledgerSummary.otherIssuesRaised?.actionsPendingOnVendor.count}
                          />
                          <SummaryLeaf
                            label="Actions Pending on Us"
                            amount={
                              ledgerSummary.otherIssuesRaised ? (
                                formatMoney(ToDineroObj(ledgerSummary.otherIssuesRaised.actionsPendingOnUs.amount))
                              ) : (
                                <WipBadge />
                              )
                            }
                            count={ledgerSummary.otherIssuesRaised?.actionsPendingOnUs.count}
                          />
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </LoggedInSkeleton>
  );
};

export default LedgerDownload;
